import { formatDate } from 'date-fns';
import { atom, computed, type WritableAtom } from 'nanostores';
import { toast } from 'react-toastify';
import {
  cancelDowngradePricingAction,
  changeIntervalPricingAction,
  downgradePricingAction,
  loginPricingAction,
  manageSubscriptionPricingAction,
  upgradePricingAction,
} from './helpers';
import { pricingStore } from './pricing';
import type {
  PricingCardAction,
  PricingCardActionId,
  PricingCardDisplayMode,
  PricingCardDropdownOption,
  PricingCardMessage,
  PricingCardType,
  PricingDisplayState,
  PricingMembershipState,
} from './types';
import type { SubscriptionInterval } from '~/types/subscription';
import { type Pricing } from '~/utils/pricing';

export abstract class PlansStore {
  displayMode: PricingCardDisplayMode = 'inactive';

  membership = atom<PricingMembershipState | null>(null);

  pricingType: PricingCardType;

  pricingList: Pricing[] = [];

  defaultPricing: Pricing;

  selectedPricing: WritableAtom<Pricing>;

  activePricing = atom<Pricing | null>(null);

  nextHigherTierPricing = computed([this.activePricing], (activePricing) => {
    if (!activePricing) {
      return null;
    }

    const nextTier = this.pricingList.find((pricing) => pricing.tier === activePricing.tier + 1);

    return nextTier ?? null;
  });

  actions = atom<PricingCardAction[]>([]);

  messages = atom<PricingCardMessage[]>([]);

  dropdownOptions = computed([this.membership], (membership) => {
    const pricingOptions: PricingCardDropdownOption[] = [];

    this.pricingList.forEach((pricing) => {
      const isActive = pricing.tier === membership?.tier;
      const isScheduled = pricing.tier === membership?.nextTier;

      pricingOptions.push({
        pricing,
        badge: isActive ? 'current' : isScheduled ? 'scheduled' : undefined,
      });
    });

    return pricingOptions;
  });

  constructor({
    defaultPricing,
    pricingList,
    pricingType,
  }: {
    defaultPricing: Pricing;
    pricingList: Pricing[];
    pricingType: PricingCardType;
  }) {
    this.defaultPricing = defaultPricing;
    this.selectedPricing = atom(defaultPricing);
    this.pricingList = pricingList;
    this.pricingType = pricingType;
  }

  protected _startRequestForPricing(id: PricingCardActionId) {
    pricingStore.startRequestForPricing(this.pricingType, id);
  }

  protected _endRequestForPricing() {
    pricingStore.endRequestForPricing();
  }

  protected getDisplayState(pricing: Pricing, interval: SubscriptionInterval): PricingDisplayState {
    const membership = this.membership.get();

    if (!membership) {
      return {
        hasSubscription: false,
        hasCanceledSubscription: false,
        isSubscribedInterval: false,
        isActive: false,
        isSameTier: false,
        isDowngraded: false,
        isDowngradedFrom: false,
        isDowngradedTo: false,
        isDowngrade: false,
        isUpgrade: false,
        isCurrentPlanCanceled: false,
        isSwitchedFromAnnualToMonthlySameTier: false,
        isDowngradedFromAnnualToMonthly: false,
        currentPeriodEnd: undefined,
        downgradedTo: undefined,
      };
    }

    const hasSubscription = (membership?.tier ?? 0) > 0;
    const hasCanceledSubscription = hasSubscription && membership?.subscription?.status === 'canceled';
    const isSubscribedInterval = interval === membership?.subscription?.interval;
    const isSameTier = membership?.tier === pricing.tier;
    const isActive = isSameTier && isSubscribedInterval;

    const isCurrentPlanCanceled = hasCanceledSubscription && membership?.tier === pricing.tier;
    const currentPeriodEnd = membership?.subscription?.currentPeriodEnd;

    const isDowngraded =
      typeof membership.tier === 'number' &&
      typeof membership.nextTier === 'number' &&
      membership.nextTier < membership.tier;

    const downgradedFrom = isDowngraded && this.pricingList.find((pricing) => pricing.tier === membership?.tier);

    const downgradedTo = isDowngraded && this.pricingList.find((pricing) => pricing.tier === membership?.nextTier);

    const isDowngradedFrom = !!downgradedFrom && downgradedFrom?.level === pricing.level;
    const isDowngradedTo = !!downgradedTo && downgradedTo?.level === pricing.level;

    const isDowngrade = (membership?.tier ?? 0) > pricing.tier;
    const isUpgrade = (membership?.tier ?? 0) < pricing.tier;

    const isSwitchedFromAnnualToMonthly = membership?.nextInterval === 'monthly';

    const isSwitchedFromAnnualToMonthlySameTier =
      isSwitchedFromAnnualToMonthly && membership?.tier === membership?.nextTier;

    const isDowngradedFromAnnualToMonthly =
      isSwitchedFromAnnualToMonthly &&
      membership &&
      typeof membership.tier === 'number' &&
      typeof membership.nextTier === 'number' &&
      membership.tier > membership.nextTier;

    return {
      hasSubscription,
      hasCanceledSubscription,
      isSubscribedInterval,
      isSameTier,
      isActive,
      isDowngraded,
      isDowngradedFrom,
      isDowngradedTo,
      isDowngrade,
      isUpgrade,
      isCurrentPlanCanceled,
      isSwitchedFromAnnualToMonthlySameTier,
      isDowngradedFromAnnualToMonthly,
      currentPeriodEnd,
      downgradedTo: downgradedTo || undefined,
    };
  }

  protected getMessages(pricing: Pricing, interval: SubscriptionInterval): PricingCardMessage[] {
    const messages: PricingCardMessage[] = [];

    const {
      hasSubscription,
      isSameTier,
      isActive,
      isDowngradedFrom,
      isCurrentPlanCanceled,
      downgradedTo,
      currentPeriodEnd,
      isSwitchedFromAnnualToMonthlySameTier,
    } = this.getDisplayState(pricing, interval);

    if (!hasSubscription) {
      return [];
    }

    const activePricing = this.activePricing.get();

    if (!!activePricing && !isSameTier) {
      messages.push({
        text: `Your current plan: ${activePricing.tokens} tokens.`,
        placement: 'under-selector',
      });
    }

    if (isDowngradedFrom && !!downgradedTo) {
      const tokens = downgradedTo.tokens;
      const planName = downgradedTo.plan === 'pro' ? `${tokens} tokens / month` : `${tokens} tokens / member / month`;

      messages.push({
        text: 'Active until the begining of the next billing cycle',
        tooltipText: `Your plan will be downgraded to ${planName} at the end of the current billing cycle`,
        placement: 'under-action',
        align: 'center',
        color: 'warning',
      });
    }

    if (isActive && isCurrentPlanCanceled && currentPeriodEnd) {
      messages.push({
        text: `This plan is active until ${formatDate(currentPeriodEnd, 'd MMMM yyyy')}`,
        placement: 'under-action',
        align: 'center',
      });
    }

    if (isSameTier && isSwitchedFromAnnualToMonthlySameTier) {
      messages.push({
        text: 'You will be switched from Annual to Monthly billing at the end of the current billing cycle',
        placement: 'under-action',
        align: 'center',
      });
    }

    return messages;
  }

  abstract changePlan(
    pricing: Pricing,
    updateType: 'upgrade' | 'downgrade' | 'change_interval',
    options?: {
      allowIntervalSelection?: boolean;
    },
  ): Promise<void>;

  abstract manageSubscription(): Promise<void>;

  abstract cancelDowngrade(pricing: Pricing): Promise<void>;

  protected getActions(pricing: Pricing, interval: SubscriptionInterval): PricingCardAction[] {
    const membership = this.membership.get();

    const actions: PricingCardAction[] = [];

    const {
      hasSubscription,
      hasCanceledSubscription,
      isSubscribedInterval,
      isSameTier,
      isActive,
      isDowngradedTo,
      isDowngrade,
      isUpgrade,
      isDowngradedFromAnnualToMonthly,
      isSwitchedFromAnnualToMonthlySameTier,
    } = this.getDisplayState(pricing, interval);

    if (!hasSubscription) {
      return [
        upgradePricingAction({
          plan: this.pricingType,
          hasCanceledSubscription,
          onUpgrade: () => this.changePlan(pricing, 'upgrade'),
        }),
      ];
    }

    if (isSameTier && !isSubscribedInterval) {
      return [
        changeIntervalPricingAction({
          plan: this.pricingType,
          hasCanceledSubscription,
          onChangeInterval: () => this.changePlan(pricing, 'change_interval'),
          disabled: isSwitchedFromAnnualToMonthlySameTier,
        }),
      ];
    }

    if (isActive) {
      actions.push(manageSubscriptionPricingAction(() => this.manageSubscription()));
    }

    if (isDowngrade) {
      if (isDowngradedFromAnnualToMonthly && membership && membership.nextInterval !== interval && isDowngradedTo) {
        const planName = pricing.namePrice ? `${pricing.name} ${pricing.namePrice}` : pricing.name;

        actions.push({
          text: 'Downgrade',
          disabled: true,
          tooltipText: `Your plan will be downgraded to ${planName} (${membership.nextInterval} billing) at the end of the current billing cycle`,
          id: 'change-plan',
        });
      } else {
        actions.push(
          downgradePricingAction({
            plan: this.pricingType,
            hasCanceledSubscription,
            onDowngrade: () => this.changePlan(pricing, 'downgrade'),
            downgradeScheduled: isDowngradedTo,
          }),
        );
      }
    }

    if (isUpgrade) {
      actions.push(
        upgradePricingAction({
          plan: this.pricingType,
          hasCanceledSubscription,
          onUpgrade: () => this.changePlan(pricing, 'upgrade'),
        }),
      );
    }

    if (isDowngradedTo) {
      actions.push(cancelDowngradePricingAction(() => this.cancelDowngrade(pricing)));
    }

    return actions;
  }

  onIntervalChange(interval: SubscriptionInterval) {
    if (this.displayMode !== 'active') {
      /**
       * We don't need to update messages or actions if we are not in active mode
       * in all other modes messages are always the same regardless of the interval.
       */
      return;
    }

    const pricing = this.selectedPricing.get();
    this.actions.set(this.getActions(pricing, interval));
    this.messages.set(this.getMessages(pricing, interval));
  }

  async upgradeToNextTier() {
    const nextTierPricing = this.nextHigherTierPricing.get();

    if (!nextTierPricing) {
      toast.error('You are already on the highest tier');
      return;
    }

    this.changePlan(nextTierPricing, 'upgrade', { allowIntervalSelection: true });
  }

  selectPricing(pricing: Pricing) {
    this.selectedPricing.set(pricing);

    if (this.displayMode !== 'active') {
      /**
       * We don't need to update messages or actions if we are not in active mode
       * in all other modes messages are always the same regardless of the interval.
       */
      return;
    }

    this.actions.set(this.getActions(pricing, pricingStore.selectedInterval.get()));
    this.messages.set(this.getMessages(pricing, pricingStore.selectedInterval.get()));
  }

  selectActivePricing() {
    const activePricing = this.activePricing.get();

    if (!activePricing) {
      return;
    }

    this.selectPricing(activePricing);
  }

  protected _initActiveState(membership: PricingMembershipState) {
    this.displayMode = 'active';
    this.membership.set(membership);

    const activePricing = this.pricingList.find((pricing) => pricing.tier === membership.tier);

    let selectedPricing = this.pricingList[0];

    if (activePricing) {
      this.activePricing.set(activePricing);

      const nextPricing = this.pricingList.find((pricing) => pricing.tier === activePricing.tier + 1);

      if (nextPricing) {
        selectedPricing = nextPricing;
      } else {
        selectedPricing = activePricing;
      }
    }

    this.selectPricing(selectedPricing);
  }

  abstract initInactiveState(inactivityReason?: string): void;

  initUnauthorizedState() {
    this.displayMode = 'unauthorized';
    this.actions.set([loginPricingAction()]);
    this.messages.set([]);
  }
}
